import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';
import { PencilIcon } from '@heroicons/react/24/outline';

type MasterDataItem = { id: string; name: string; unit?: string };
type CategoryKey = 'feed_brands' | 'medicine_types' | 'goat_feed_brands' | 'goat_medicine_types' | 'other_items';
type InventoryCategory = 'feed' | 'medicine' | 'other';

type InventoryPurchase = {
  id: string;
  location_id: string;
  item_id: string;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  quantity_remaining: number;
  purchase_date: string;
  pond_id?: string | null;
  created_at?: string;
  updated_at?: string;
};

type InventoryItem = {
  id: string;
  name: string;
  unit: string;
  category: string;
};

const InventoryPurchases: React.FC = () => {
  const { user } = useAuth();
  const { selectedLocation } = useLocation();
  const [category, setCategory] = useState<CategoryKey>('feed_brands');
  const [masterMedicine, setMasterMedicine] = useState<MasterDataItem[]>([]);
  const [masterFeed, setMasterFeed] = useState<MasterDataItem[]>([]);
  const [masterGoatFeed, setMasterGoatFeed] = useState<MasterDataItem[]>([]);
  const [masterGoatMedicine, setMasterGoatMedicine] = useState<MasterDataItem[]>([]);
  const [masterOther, setMasterOther] = useState<MasterDataItem[]>([]);
  const [selectedMasterId, setSelectedMasterId] = useState('');
  const [purchaseQty, setPurchaseQty] = useState('');
  const [purchaseUnit, setPurchaseUnit] = useState('');
  const [totalCost, setTotalCost] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [purchaseDate, setPurchaseDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [purchases, setPurchases] = useState<InventoryPurchase[]>([]);
  const [inventoryItems, setInventoryItems] = useState<Record<string, InventoryItem>>({});
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<InventoryPurchase | null>(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [editUnitCost, setEditUnitCost] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState('');
  const [editQuantityRemaining, setEditQuantityRemaining] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState<InventoryCategory | 'all'>('all');

  useEffect(() => {
    const load = async () => {
      if (!selectedLocation?.id) {
        setMasterMedicine([]);
        setMasterFeed([]);
        setMasterGoatMedicine([]);
        setMasterGoatFeed([]);
        setMasterOther([]);
        return;
      }
      try {
        const [med, feed, goatMed, goatFeed, other] = await Promise.all([
          apiClient.get('/master-data/medicine_types', { params: { location_id: selectedLocation.id } }),
          apiClient.get('/master-data/feed_brands', { params: { location_id: selectedLocation.id } }),
          apiClient.get('/master-data/goat_medicine_types', { params: { location_id: selectedLocation.id } }),
          apiClient.get('/master-data/goat_feed_brands', { params: { location_id: selectedLocation.id } }),
          apiClient.get('/master-data/other_items', { params: { location_id: selectedLocation.id } }),
        ]);
        setMasterMedicine(med.data || []);
        setMasterFeed(feed.data || []);
        setMasterGoatMedicine(goatMed.data || []);
        setMasterGoatFeed(goatFeed.data || []);
        setMasterOther(other.data || []);
      } catch (e) {
        // ignore; page can still function if empty
      }
    };
    load();
  }, [selectedLocation?.id]);

  // Load purchases and inventory items
  useEffect(() => {
    if (!selectedLocation) {
      setPurchases([]);
      return;
    }
    loadPurchases();
  }, [selectedLocation]);

  const loadPurchases = async () => {
    if (!selectedLocation) return;
    setLoadingPurchases(true);
    try {
      const [purchasesRes, itemsRes] = await Promise.all([
        apiClient.get('/inventory/purchases', { params: { location_id: selectedLocation.id } }),
        apiClient.get('/inventory/items', { params: { location_id: selectedLocation.id } }),
      ]);
      setPurchases(purchasesRes.data || []);
      // Create a map of item_id -> item for quick lookup
      const itemsMap: Record<string, InventoryItem> = {};
      (itemsRes.data || []).forEach((item: InventoryItem) => {
        itemsMap[item.id] = item;
      });
      setInventoryItems(itemsMap);
    } catch (e) {
      console.error('Failed to load purchases:', e);
    } finally {
      setLoadingPurchases(false);
    }
  };

  const options = useMemo(() => {
    if (category === 'medicine_types') return masterMedicine;
    if (category === 'feed_brands') return masterFeed;
    if (category === 'goat_medicine_types') return masterGoatMedicine;
    if (category === 'goat_feed_brands') return masterGoatFeed;
    if (category === 'other_items') return masterOther;
    return [];
  }, [category, masterMedicine, masterFeed, masterGoatMedicine, masterGoatFeed, masterOther]);

  // Conversion function to convert purchase unit to item's base unit
  const convertToBaseUnit = (purchaseQty: number, purchaseUnit: string, itemUnit: string): { convertedQty: number; error: string | null } => {
    if (!purchaseUnit || !itemUnit) {
      return { convertedQty: 0, error: null };
    }

    const normalizedPurchaseUnit = purchaseUnit.toLowerCase().trim();
    const normalizedItemUnit = itemUnit.toLowerCase().trim();

    // If units match, no conversion needed
    if (normalizedPurchaseUnit === normalizedItemUnit) {
      return { convertedQty: purchaseQty, error: null };
    }

    // Conversion rules based on item unit
    if (normalizedItemUnit === 'g') {
      if (normalizedPurchaseUnit === 'kg') {
        return { convertedQty: purchaseQty * 1000, error: null };
      } else if (normalizedPurchaseUnit === 'g') {
        return { convertedQty: purchaseQty, error: null };
      } else {
        return { convertedQty: 0, error: `Cannot convert ${purchaseUnit} to ${itemUnit}. Please use kg or g.` };
      }
    }

    if (normalizedItemUnit === 'ml') {
      if (normalizedPurchaseUnit === 'liter' || normalizedPurchaseUnit === 'l') {
        return { convertedQty: purchaseQty * 1000, error: null };
      } else if (normalizedPurchaseUnit === 'ml') {
        return { convertedQty: purchaseQty, error: null };
      } else {
        return { convertedQty: 0, error: `Cannot convert ${purchaseUnit} to ${itemUnit}. Please use liter or ml.` };
      }
    }

    if (normalizedItemUnit === 'kg') {
      if (normalizedPurchaseUnit === 'kg') {
        return { convertedQty: purchaseQty, error: null };
      } else if (normalizedPurchaseUnit === 'g') {
        return { convertedQty: purchaseQty / 1000, error: null };
      } else {
        return { convertedQty: 0, error: `Cannot convert ${purchaseUnit} to ${itemUnit}. Please use kg or g.` };
      }
    }

    // For tablets, bottle, or other units, must match exactly
    if (normalizedItemUnit === 'bottle' || normalizedItemUnit === 'tablets') {
      if (normalizedPurchaseUnit !== normalizedItemUnit) {
        return { convertedQty: 0, error: `Purchase unit (${purchaseUnit}) must match item unit (${itemUnit}).` };
      }
      return { convertedQty: purchaseQty, error: null };
    }

    // For other units that don't have conversion rules, must match exactly
    if (normalizedPurchaseUnit !== normalizedItemUnit) {
      return { convertedQty: 0, error: `Purchase unit (${purchaseUnit}) must match item unit (${itemUnit}).` };
    }

    return { convertedQty: purchaseQty, error: null };
  };

  // Auto-calculate quantity and unit cost from purchase inputs
  useEffect(() => {
    setConversionError(null);
    
    if (!selectedMasterId || !purchaseQty || !purchaseUnit || !totalCost) {
      setQuantity('');
      setUnitCost('');
      return;
    }

    const selectedItem = options.find(o => o.id === selectedMasterId);
    if (!selectedItem) {
      setQuantity('');
      setUnitCost('');
      return;
    }

    const itemUnit = selectedItem.unit || '';
    if (!itemUnit) {
      setQuantity('');
      setUnitCost('');
      return;
    }

    const purchaseQtyNum = parseFloat(purchaseQty);
    const totalCostNum = parseFloat(totalCost);

    // Validate inputs
    if (isNaN(purchaseQtyNum) || purchaseQtyNum <= 0) {
      setQuantity('');
      setUnitCost('');
      return;
    }

    if (isNaN(totalCostNum) || totalCostNum <= 0) {
      setQuantity('');
      setUnitCost('');
      return;
    }

    // Convert purchase quantity to base unit
    const { convertedQty, error } = convertToBaseUnit(purchaseQtyNum, purchaseUnit, itemUnit);
    
    if (error) {
      setConversionError(error);
      setQuantity('');
      setUnitCost('');
      return;
    }

    if (convertedQty <= 0) {
      setQuantity('');
      setUnitCost('');
      return;
    }

    // Calculate unit cost
    const calculatedUnitCost = totalCostNum / convertedQty;

    setQuantity(convertedQty.toFixed(4));
    setUnitCost(calculatedUnitCost.toFixed(4));
  }, [purchaseQty, purchaseUnit, totalCost, selectedMasterId, options]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation) return;
    if (!selectedMasterId) {
      setMessage('Please select an item');
      return;
    }
    if (!purchaseQty || !purchaseUnit || !totalCost) {
      setMessage('Please fill in all purchase fields');
      return;
    }
    if (conversionError) {
      setMessage(conversionError);
      return;
    }
    if (!quantity || !unitCost) {
      setMessage('Please ensure all fields are filled correctly');
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      // Determine inventory item
      let invItemId = '';
      let invCategory: InventoryCategory = 'other';
      let invName = '';
      let invUnit = '';

      const md = options.find(o => o.id === selectedMasterId);
      if (!md) throw new Error('Select a master item');
      if (category === 'feed_brands' || category === 'goat_feed_brands') invCategory = 'feed';
      if (category === 'medicine_types' || category === 'goat_medicine_types') invCategory = 'medicine';
      if (category === 'other_items') invCategory = 'other';
      invName = md.name;
      invUnit = md.unit || (invCategory === 'feed' ? 'kg' : invCategory === 'medicine' ? 'ml' : 'piece');

      // Try find existing inventory item by name+category at location
      const list = await apiClient.get('/inventory/items', { params: { location_id: selectedLocation.id } });
      const found = (list.data || []).find((it: any) => it.name === invName && it.category === invCategory);
      if (found) {
        invItemId = found.id;
      } else {
        // Check if user has admin or manager access before attempting to create item
        if (user?.role !== 'admin' && user?.role !== 'manager') {
          throw new Error(`Inventory item "${invName}" does not exist. Admin or Manager access is required to create new inventory items. Please contact an administrator.`);
        }
        try {
          const created = await apiClient.post('/inventory/items', {
            location_id: selectedLocation.id,
            name: invName,
            category: invCategory,
            unit: invUnit,
            is_active: true,
            created_by: user?.id || 'system',
            updated_by: user?.id || 'system',
          });
          invItemId = created.data.id;
        } catch (itemErr: any) {
          if (itemErr?.response?.status === 403) {
            throw new Error(`Admin or Manager access required to create inventory item "${invName}". Please contact an administrator.`);
          }
          throw itemErr;
        }
      }

      await apiClient.post('/inventory/purchases', {
        location_id: selectedLocation.id,
        item_id: invItemId,
        quantity: parseFloat(quantity),
        unit_cost: parseFloat(unitCost),
        purchase_date: purchaseDate,
      });
      setMessage('Purchase recorded');
      // Clear all purchase fields
      setPurchaseQty('');
      setPurchaseUnit('');
      setTotalCost('');
      setQuantity('');
      setUnitCost('');
      setConversionError(null);
      // Refresh purchases list
      await loadPurchases();
    } catch (err: any) {
      setMessage(err?.response?.data?.detail || 'Failed to record purchase');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (purchase: InventoryPurchase) => {
    setEditingPurchase(purchase);
    setEditQuantity(purchase.quantity.toString());
    setEditUnitCost(purchase.unit_cost.toString());
    setEditPurchaseDate(purchase.purchase_date);
    setEditQuantityRemaining(purchase.quantity_remaining.toString());
  };

  const handleCancelEdit = () => {
    setEditingPurchase(null);
    setEditQuantity('');
    setEditUnitCost('');
    setEditPurchaseDate('');
    setEditQuantityRemaining('');
  };

  const handleUpdatePurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPurchase) return;
    
    // Validate quantity_remaining doesn't exceed quantity
    const qtyRemaining = parseFloat(editQuantityRemaining);
    const qty = parseFloat(editQuantity);
    if (qtyRemaining > qty) {
      setMessage('Quantity remaining cannot exceed total quantity');
      return;
    }
    if (qtyRemaining < 0) {
      setMessage('Quantity remaining cannot be negative');
      return;
    }
    
    setEditLoading(true);
    try {
      await apiClient.put(`/inventory/purchases/${editingPurchase.id}`, {
        quantity: qty,
        unit_cost: parseFloat(editUnitCost),
        purchase_date: editPurchaseDate,
        quantity_remaining: qtyRemaining,
      });
      setMessage('Purchase updated successfully');
      setEditingPurchase(null);
      await loadPurchases();
    } catch (err: any) {
      setMessage(err?.response?.data?.detail || 'Failed to update purchase');
    } finally {
      setEditLoading(false);
    }
  };

  const selectedItem = options.find(o => o.id === selectedMasterId);
  const displayUnit = selectedItem?.unit || '';

  // Helper function to get category display name
  const getCategoryDisplayName = (category: string, itemName: string): string => {
    if (category === 'feed') {
      // Check if it's goat feed by checking master data
      const isGoatFeed = masterGoatFeed.some(f => f.name === itemName);
      return isGoatFeed ? 'Goat Feed' : 'Fish Feed';
    }
    if (category === 'medicine') {
      // Check if it's goat medicine by checking master data
      const isGoatMedicine = masterGoatMedicine.some(m => m.name === itemName);
      return isGoatMedicine ? 'Goat Medicine' : 'Fish Medicine';
    }
    if (category === 'other') {
      return 'Other Items';
    }
    return category;
  };

  // Filter purchases by category
  const filteredPurchases = filterCategory === 'all' 
    ? purchases 
    : purchases.filter(purchase => {
        const item = inventoryItems[purchase.item_id];
        return item?.category === filterCategory;
      });

  return (
    <div className="container-mobile py-4 px-4 space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Inventory Purchases</h2>
      <form onSubmit={submit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-5">
        {/* Category and Item Row */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select 
              value={category} 
              onChange={(e) => { 
                setCategory(e.target.value as CategoryKey); 
                setSelectedMasterId('');
                // Reset purchase fields when category changes
                setPurchaseQty('');
                setPurchaseUnit('');
                setTotalCost('');
                setQuantity('');
                setUnitCost('');
                setConversionError(null);
              }} 
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="feed_brands">Fish Feed</option>
              <option value="medicine_types">Fish Medicine</option>
              <option value="goat_feed_brands">Goat Feed</option>
              <option value="goat_medicine_types">Goat Medicine</option>
              <option value="other_items">Other Items</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Item</label>
            <select 
              value={selectedMasterId} 
              onChange={(e) => {
                setSelectedMasterId(e.target.value);
                // Reset purchase fields when item changes
                setPurchaseQty('');
                setPurchaseUnit('');
                setTotalCost('');
                setQuantity('');
                setUnitCost('');
                setConversionError(null);
              }} 
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select {category.replace('_', ' ')}</option>
              {options.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name}{it.unit ? ` (${it.unit})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Purchase Unit, Purchase Qty, and Total Cost */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Unit</label>
            <select 
              value={purchaseUnit} 
              onChange={(e) => setPurchaseUnit(e.target.value)} 
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select Purchase Unit</option>
              {category === 'goat_medicine_types' && (
                <option value="bottle">Bottle</option>
              )}
              <option value="kg">Kilograms (kg)</option>
              <option value="liter">Liter</option>
              <option value="g">Grams (g)</option>
              <option value="ml">Milliliters (ml)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Qty</label>
            <input 
              value={purchaseQty} 
              onChange={(e) => setPurchaseQty(e.target.value)} 
              type="number" 
              step="0.0001" 
              placeholder="0.0000"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Total Cost (₹)</label>
            <input 
              value={totalCost} 
              onChange={(e) => setTotalCost(e.target.value)} 
              type="number" 
              step="0.01" 
              placeholder="0.00"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
            />
          </div>
        </div>

        {/* Converted Quantity and Unit Cost (Readonly) */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity {displayUnit && <span className="text-gray-500">({displayUnit})</span>}
            </label>
            <input 
              value={quantity} 
              readOnly
              type="number" 
              step="0.0001" 
              placeholder="Auto-calculated"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base bg-gray-50 text-gray-600 cursor-not-allowed" 
            />
            <p className="text-xs text-gray-500 mt-1">Automatically calculated from purchase qty</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit Cost {displayUnit && <span className="text-gray-500">(₹/{displayUnit})</span>}
            </label>
            <input 
              value={unitCost} 
              readOnly
              type="number" 
              step="0.0001" 
              placeholder="Auto-calculated"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base bg-gray-50 text-gray-600 cursor-not-allowed" 
            />
            <p className="text-xs text-gray-500 mt-1">Automatically calculated from total cost</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
            <input 
              value={purchaseDate} 
              onChange={(e) => setPurchaseDate(e.target.value)} 
              type="date" 
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
            />
          </div>
        </div>

        {/* Conversion Error Display */}
        {conversionError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{conversionError}</p>
          </div>
        )}

        {/* Submit Button */}
        <button 
          disabled={loading} 
          className="w-full bg-blue-600 text-white rounded-lg px-4 py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 active:bg-blue-800 transition-colors"
        >
          {loading ? 'Saving...' : 'Save Purchase'}
        </button>
      </form>
      
      {/* Message Display */}
      {message && (
        <div className={`text-sm p-3 rounded-lg ${
          message.includes('Failed') || message.includes('Please') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message}
        </div>
      )}

      {/* Purchases List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Purchases</h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
            <label className="text-sm text-gray-700 font-medium whitespace-nowrap">Filter by Category:</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as InventoryCategory | 'all')}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white w-full sm:w-auto"
            >
              <option value="all">All Categories</option>
              <option value="feed">Feed (Fish & Goat)</option>
              <option value="medicine">Medicine (Fish & Goat)</option>
              <option value="other">Other Items</option>
            </select>
          </div>
        </div>
        {loadingPurchases ? (
          <div className="text-center py-8 text-gray-500">Loading purchases...</div>
        ) : filteredPurchases.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {purchases.length === 0 ? 'No purchases found' : `No purchases found for selected category`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Category</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Item</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Quantity</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Unit Cost</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Total Cost</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Remaining</th>
                  {user?.role === 'admin' && (
                    <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredPurchases.map((purchase) => {
                  const item = inventoryItems[purchase.item_id];
                  const categoryDisplayName = item ? getCategoryDisplayName(item.category, item.name) : 'Unknown';
                  return (
                    <tr key={purchase.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-900">
                        {new Date(purchase.purchase_date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {categoryDisplayName}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-900">
                        {item ? `${item.name} (${item.unit})` : 'Unknown Item'}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        {purchase.quantity.toFixed(4)} {item?.unit || ''}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        ₹{purchase.unit_cost.toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 font-medium">
                        ₹{purchase.total_cost.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-600">
                        {purchase.quantity_remaining.toFixed(4)} {item?.unit || ''}
                      </td>
                      {user?.role === 'admin' && (
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => handleEdit(purchase)}
                            className="text-blue-600 hover:text-blue-800 p-2 rounded-md border border-gray-200 hover:border-blue-200 hover:bg-blue-50"
                            title="Edit purchase"
                            aria-label="Edit purchase"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingPurchase && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCancelEdit}
        >
          <div 
            className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900">Edit Purchase</h3>
            <form onSubmit={handleUpdatePurchase} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item
                </label>
                <input
                  type="text"
                  value={inventoryItems[editingPurchase.item_id]?.name || 'Unknown'}
                  disabled
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity ({inventoryItems[editingPurchase.item_id]?.unit || ''})
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit Cost (₹/{inventoryItems[editingPurchase.item_id]?.unit || ''})
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={editUnitCost}
                  onChange={(e) => setEditUnitCost(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Date
                </label>
                <input
                  type="date"
                  value={editPurchaseDate}
                  onChange={(e) => setEditPurchaseDate(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity Remaining ({inventoryItems[editingPurchase.item_id]?.unit || ''})
                  <span className="text-xs text-gray-500 ml-2">(Can be edited directly)</span>
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={editQuantityRemaining}
                  onChange={(e) => setEditQuantityRemaining(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {parseFloat(editQuantityRemaining) > parseFloat(editQuantity) && (
                  <p className="mt-1 text-sm text-red-600">
                    Quantity remaining cannot exceed total quantity
                  </p>
                )}
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editLoading ? 'Updating...' : 'Update Purchase'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPurchases;


